import type { Map as MapLibreMap } from 'maplibre-gl';
import type { CameraPadding } from '../domain/camera-plan';

export interface CoverageFrame {
  width: number;
  height: number;
  fieldOfView: number;
  roll: number;
  padding: CameraPadding;
}

/** Lowest downward component of the four camera rays, including the off-center lens. */
export function minimumGroundRay(pitch: number, frame: CoverageFrame): number {
  const radians = Math.PI / 180;
  const distance = frame.height / (2 * Math.tan(frame.fieldOfView * radians / 2));
  const cx = (frame.width + frame.padding.left - frame.padding.right) / 2;
  const cy = (frame.height + frame.padding.top - frame.padding.bottom) / 2;
  const sin = Math.sin(frame.roll * radians), cos = Math.cos(frame.roll * radians);
  const edge = Math.min(...[0, frame.width].flatMap(x => [0, frame.height].map(y =>
    (sin * (x - cx) + cos * (y - cy)) / distance)));
  return Math.cos(pitch * radians) + Math.sin(pitch * radians) * edge;
}

interface CoverageTransform {
  nearZ: number;
  farZ: number;
  width: number;
  height: number;
  fov: number;
  pitch: number;
  roll: number;
  padding: CameraPadding;
  pixelsPerMeter: number;
  minElevationForCurrentTile: number;
  getCameraAltitude(): number;
  overrideNearFarZ(near: number, far: number): void;
  clearNearFarZOverride(): void;
}

const overridden = new WeakSet<MapLibreMap>();

function cameraTransform(map: MapLibreMap): CoverageTransform | undefined {
  // MapLibre exposes depth-range control on its transform, rather than on Map.
  return (map as unknown as { _camera?: { transform?: CoverageTransform } })._camera?.transform;
}

/** Restore automatic depth calculation before resolving another camera pose. */
export function clearMapFrameCoverage(map: MapLibreMap): void {
  if (!overridden.delete(map)) return;
  cameraTransform(map)?.clearNearFarZOverride();
}

/** Include the most distant ground corner after bank and asymmetric overlay padding. */
export function applyMapFrameCoverage(map: MapLibreMap): void {
  const transform = cameraTransform(map);
  if (!transform) return;
  // Camera updates can copy an earlier override through MapLibre's requested transform.
  transform.clearNearFarZOverride();
  const downward = minimumGroundRay(transform.pitch, { width: transform.width, height: transform.height,
    fieldOfView: transform.fov, roll: transform.roll, padding: transform.padding });
  const altitude = transform.getCameraAltitude();
  const floor = Math.min(0, transform.minElevationForCurrentTile, altitude - 100);
  const groundDepth = (altitude - floor) * transform.pixelsPerMeter / Math.max(downward, Math.sin(.1 * Math.PI / 180));
  const far = Math.max(transform.farZ, groundDepth * 1.02);
  if (!Number.isFinite(far)) throw new Error('Camera frame coverage received an invalid ground distance.');
  transform.overrideNearFarZ(transform.nearZ, far);
  overridden.add(map);
}
